import { useEffect, useRef, useState } from "react";
import { Check, Copy, ThumbsDown, ThumbsUp } from "lucide-react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { FeedbackInfo, Part, ToolPart } from "../types";
import { ToolCallRenderer } from "./tool-renderers";
import { StreamingPulse } from "./StreamingPulse";
import { useFeedback } from "../hooks/use-feedback";

const MARKDOWN_PLUGINS = [remarkGfm];
// Reveal text at the source's *sustained* rate rather than proportional to the
// backlog. Bursty upstreams (e.g. the Evo gateway, which flushes tokens in
// clumps separated by ~0.5s stalls) otherwise dump each clump in a few frames
// then starve — the eye reads that as ragged. Pacing at the average rate
// spreads each clump across the following stall, so the buffer self-stabilises
// at ~one clump and the stalls stay hidden. Latency is bounded by MAX_LAG_MS,
// and average display speed equals arrival speed, so streaming is not slowed.
const SNAP_BACKLOG_CHARS = 240; // huge jump (final replace / paste) — show at once
const MAX_LAG_MS = 600; // never let the display trail the source by more than this
const DEFAULT_RATE = 0.12; // chars/ms (~120 ch/s) until we have a real estimate
const MIN_RATE = 0.02; // floor so a slow start still progresses
const MIN_ELAPSED_MS = 120; // gather this much before trusting the measured rate

function now() {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

function StreamingMarkdown({ text }: { text: string }) {
  const [displayed, setDisplayed] = useState(text);
  const targetRef = useRef(text);
  const displayedRef = useRef(text);
  const rafRef = useRef<number | null>(null);
  // Sustained-rate estimator: chars produced since the first delta, over
  // wall-clock since then. Includes the stalls, which is exactly what makes
  // each clump drain over the gap that follows it.
  const startLenRef = useRef<number | null>(null);
  const startTimeRef = useRef(0);
  const lastTickRef = useRef(0);

  useEffect(() => {
    targetRef.current = text;
    // Edits/replacements (not pure append) — snap to avoid stale prefix.
    if (!text.startsWith(displayedRef.current)) {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      displayedRef.current = text;
      setDisplayed(text);
      startLenRef.current = null; // restart the rate estimate on replacement
      return;
    }
    if (displayedRef.current.length >= text.length) return;
    if (startLenRef.current === null) {
      startLenRef.current = displayedRef.current.length;
      startTimeRef.current = now();
    }
    if (rafRef.current != null) return;
    lastTickRef.current = now();
    const tick = () => {
      const t = now();
      const target = targetRef.current;
      const cur = displayedRef.current;
      const backlog = target.length - cur.length;
      if (backlog <= 0) {
        rafRef.current = null;
        return;
      }
      const dt = Math.max(1, t - lastTickRef.current);
      lastTickRef.current = t;

      let step: number;
      if (backlog > SNAP_BACKLOG_CHARS) {
        step = backlog;
      } else {
        const elapsed = t - startTimeRef.current;
        const produced = target.length - (startLenRef.current ?? 0);
        const rate =
          elapsed >= MIN_ELAPSED_MS && produced > 0
            ? Math.max(MIN_RATE, produced / elapsed)
            : DEFAULT_RATE;
        let reveal = rate * dt;
        // Latency guard: if the backlog represents more than MAX_LAG_MS at the
        // current rate, trim the excess so the display can't fall far behind.
        const excess = backlog - rate * MAX_LAG_MS;
        if (excess > 0) reveal += excess / 4;
        step = Math.min(backlog, Math.max(1, Math.ceil(reveal)));
      }
      const nextLen = Math.min(cur.length + step, target.length);
      const next = nextLen >= target.length ? target : target.slice(0, nextLen);
      displayedRef.current = next;
      setDisplayed(next);
      rafRef.current = next.length < target.length ? requestAnimationFrame(tick) : null;
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [text]);

  useEffect(() => () => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
  }, []);

  return <Markdown remarkPlugins={MARKDOWN_PLUGINS}>{displayed}</Markdown>;
}

function PartRenderer({ part }: { part: Part }) {
  switch (part.type) {
    case "reasoning":
      return (
        <details className="reasoning-part">
          <summary>Рассуждение</summary>
          <div>
            <Markdown remarkPlugins={MARKDOWN_PLUGINS}>{part.text}</Markdown>
          </div>
        </details>
      );
    case "tool":
      return <ToolCallRenderer part={part} />;
    case "text":
      return <StreamingMarkdown text={part.text} />;
  }
}

// Rounded-square-with-slash — same glyph as the composer's "Сценарии"
// button, so a sent command reads as the same affordance it came from.
const CMD_ICON_PATH =
  "M11.167 0C12.1793 0.000175703 12.9998 0.820744 13 1.83301V11.167C12.9998 12.1793 12.1793 12.9998 11.167 13H1.83301C0.820744 12.9998 0.000175704 12.1793 0 11.167V1.83301C0.000175913 0.820744 0.820744 0.000175913 1.83301 0H11.167ZM1.83301 1C1.37303 1.00018 1.00018 1.37303 1 1.83301V11.167C1.00018 11.627 1.37303 11.9998 1.83301 12H11.167C11.627 11.9998 11.9998 11.627 12 11.167V1.83301C11.9998 1.37303 11.627 1.00018 11.167 1H1.83301ZM8.14648 4.14648C8.34175 3.95122 8.65825 3.95122 8.85352 4.14648C9.04878 4.34175 9.04878 4.65825 8.85352 4.85352L4.85352 8.85352C4.65825 9.04878 4.34175 9.04878 4.14648 8.85352C3.95122 8.65825 3.95122 8.34175 4.14648 8.14648L8.14648 4.14648Z";

/** A user message sent via a slash command — collapsed to a compact chip,
 *  expands to reveal the full prompt that actually went to the agent. */
function UserCommandMessage({ label, text }: { label: string; text: string }) {
  return (
    <details className="msg msg-user-cmd">
      <summary>
        <svg
          className="msg-user-cmd-icon"
          viewBox="0 0 13 13"
          width="13"
          height="13"
          fill="none"
          aria-hidden="true"
        >
          <path d={CMD_ICON_PATH} fill="currentColor" />
        </svg>
        <span className="msg-user-cmd-label">{label}</span>
        <svg
          className="msg-user-cmd-chevron"
          viewBox="0 0 16 16"
          width="14"
          height="14"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M4 6l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </summary>
      <div className="msg-user-cmd-body">{text}</div>
    </details>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <button type="button" className="msg-toolbar-btn" onClick={handleCopy} title="Скопировать ответ">
      {copied ? (
        <>
          <Check size={14} strokeWidth={2} />
          Скопировано
        </>
      ) : (
        <>
          <Copy size={14} strokeWidth={2} />
          Копировать
        </>
      )}
    </button>
  );
}

function FeedbackButtons({
  sessionId,
  traceId,
  feedback,
}: {
  sessionId: string;
  traceId: string;
  feedback: FeedbackInfo | null | undefined;
}) {
  const { submit, clear } = useFeedback(sessionId);
  const active = feedback?.score ?? 0;
  // Which vote the comment popover is open for; null = closed.
  const [draft, setDraft] = useState<1 | -1 | null>(null);
  const [comment, setComment] = useState("");
  const popoverRef = useRef<HTMLDivElement | null>(null);

  // Dismiss the popover on outside click or Esc — the vote is already
  // persisted by the time it opens, so dismissing just skips the comment.
  useEffect(() => {
    if (draft === null) return;
    const onDown = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setDraft(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDraft(null);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [draft]);

  const onVote = (score: 1 | -1) => {
    if (active === score) {
      clear(traceId);
      setDraft(null);
      return;
    }
    // Register the vote immediately, then ask for an optional comment.
    submit(traceId, score);
    setComment("");
    setDraft(score);
  };

  const sendComment = () => {
    if (draft === null) return;
    submit(traceId, draft, comment);
    setDraft(null);
  };

  return (
    <div className="feedback">
      <button
        type="button"
        className={`msg-toolbar-btn${active === 1 ? " is-active" : ""}`}
        onClick={() => onVote(1)}
        title="Хороший ответ"
        aria-pressed={active === 1}
      >
        <ThumbsUp size={14} strokeWidth={2} fill={active === 1 ? "currentColor" : "none"} />
      </button>
      <button
        type="button"
        className={`msg-toolbar-btn${active === -1 ? " is-active" : ""}`}
        onClick={() => onVote(-1)}
        title="Плохой ответ"
        aria-pressed={active === -1}
      >
        <ThumbsDown size={14} strokeWidth={2} fill={active === -1 ? "currentColor" : "none"} />
      </button>
      {draft !== null && (
        <div className="feedback-popover" ref={popoverRef}>
          <div className="feedback-popover__title">
            {draft === 1 ? "Что понравилось?" : "Что пошло не так?"}
          </div>
          <textarea
            className="feedback-popover__input"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Комментарий…"
            rows={3}
            maxLength={4000}
            autoFocus
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") sendComment();
            }}
          />
          <div className="feedback-popover__actions">
            <button
              type="button"
              className="feedback-popover__skip"
              onClick={() => setDraft(null)}
            >
              Пропустить
            </button>
            <button
              type="button"
              className="feedback-popover__send"
              onClick={sendComment}
            >
              Отправить
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface Props {
  parts: Part[];
  role: "user" | "assistant";
  isStreaming?: boolean;
  sessionId?: string;
  traceId?: string | null;
  feedback?: FeedbackInfo | null;
}

export function TurnView({
  parts,
  role,
  isStreaming = false,
  sessionId,
  traceId,
  feedback,
}: Props) {
  if (role === "user") {
    const textParts = parts.filter(
      (p): p is Extract<Part, { type: "text" }> => p.type === "text",
    );
    const text = textParts.map((p) => p.text).join("\n");
    const command = textParts.find((p) => p.command)?.command;
    if (command) return <UserCommandMessage label={command.label} text={text} />;
    return <div className="msg msg-user">{text}</div>;
  }

  const plainText = parts
    .filter((p): p is Extract<Part, { type: "text" }> => p.type === "text")
    .map((p) => p.text)
    .join("\n")
    .trim();

  const showToolbar = !isStreaming && plainText.length > 0;

  return (
    <div className="msg msg-assistant">
      {parts.map((part, i) => (
        <PartRenderer key={part.type === "tool" ? (part as ToolPart).id : i} part={part} />
      ))}
      {isStreaming && <StreamingPulse />}
      {showToolbar && (
        <div className="msg-toolbar">
          <CopyButton text={plainText} />
          {traceId && sessionId && (
            <FeedbackButtons sessionId={sessionId} traceId={traceId} feedback={feedback} />
          )}
        </div>
      )}
    </div>
  );
}
